import React, { useState, useRef } from 'react';
import {
    Container,
    Typography,
    Box,
    Grid,
    TextField,
    Button,
    Paper,
    Snackbar,
    Alert,
    CircularProgress
} from '@mui/material';
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import './Contact.scss';

// Add TypeScript declaration for EmailJS
declare global {
    interface Window {
        emailjs: {
            send: (
                serviceId: string,
                templateId: string,
                templateParams: Record<string, any>,
                userId: string
            ) => Promise<any>;
        };
    }
}

const Contact: React.FC = () => {
    const formRef = useRef<HTMLFormElement>(null);
    const [formState, setFormState] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });

    const [errors, setErrors] = useState({
        name: false,
        email: false,
        message: false
    });

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success' as 'success' | 'error'
    });

    const [loading, setLoading] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormState(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user types
        if (errors[name as keyof typeof errors]) {
            setErrors(prev => ({
                ...prev,
                [name]: false
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {
            name: formState.name.trim() === '',
            email: !/^\S+@\S+\.\S+$/.test(formState.email),
            message: formState.message.trim() === ''
        };

        setErrors(newErrors);
        return !Object.values(newErrors).some(error => error);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (validateForm()) {
            setLoading(true);

            try {
                // Option 1: Use EmailJS (add the script to public/index.html)
                // <script type="text/javascript" src="https://cdn.emailjs.com/sdk/2.3.2/email.min.js"></script>
                if (window.emailjs) {
                    const templateParams = {
                        name: formState.name,
                        email: formState.email,
                        subject: formState.subject || 'Contact Form Submission',
                        message: formState.message,
                        to_email: 'contact@myprojectx.com'
                    };

                    await window.emailjs.send(
                        'YOUR_SERVICE_ID', // Create an EmailJS account and get these values
                        'YOUR_TEMPLATE_ID',
                        templateParams,
                        'YOUR_USER_ID'
                    );

                    // Show success message
                    setSnackbar({
                        open: true,
                        message: 'Thank you for your message! We will get back to you soon.',
                        severity: 'success'
                    });

                    // Reset form
                    setFormState({
                        name: '',
                        email: '',
                        subject: '',
                        message: ''
                    });
                } else {
                    // Option 2: You can also use a form submission service like Formspree
                    const form = formRef.current;
                    const formData = new FormData(form!);

                    const response = await fetch('https://formspree.io/f/xdabzodv', {
                        method: 'POST',
                        body: formData,
                        headers: {
                            Accept: 'application/json'
                        }
                    });

                    if (response.ok) {
                        // Show success message
                        setSnackbar({
                            open: true,
                            message: 'Thank you for your message! We will get back to you soon.',
                            severity: 'success'
                        });

                        // Reset form
                        setFormState({
                            name: '',
                            email: '',
                            subject: '',
                            message: ''
                        });
                    } else {
                        throw new Error('Failed to send message');
                    }
                }
            } catch (error) {
                console.error('Error sending form:', error);
                setSnackbar({
                    open: true,
                    message: 'Failed to send message. Please try again or contact us directly at myprojectxinc@gmail.com',
                    severity: 'error'
                });
            } finally {
                setLoading(false);
            }
        } else {
            setSnackbar({
                open: true,
                message: 'Please correct the errors in the form.',
                severity: 'error'
            });
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({
            ...prev,
            open: false
        }));
    };

    const helpTopics = [
        {
            icon: <QrCode2RoundedIcon />,
            title: 'Get a Quick Setup code',
            body: 'Send us your rotation (the text for each day until it repeats) and a name for it. We\'ll create a 4-digit code your whole team can use.',
        },
        {
            icon: <SupportAgentRoundedIcon />,
            title: 'App support',
            body: 'Questions about ShiftWave or CopApp!, subscriptions or restoring a purchase? We\'re happy to help.',
        },
        {
            icon: <MailOutlineRoundedIcon />,
            title: 'Email us directly',
            body: 'myprojectxinc@gmail.com',
            href: 'mailto:myprojectxinc@gmail.com',
        },
    ];

    return (
        <div className="contact-page">
            <Container maxWidth="lg" className="contact-page__inner">
                <Box className="contact-page__intro">
                    <span className="eyebrow">Contact</span>
                    <Typography variant="h2" component="h1">
                        We'd love to hear from you
                    </Typography>
                    <Typography className="contact-page__lead">
                        Questions, feedback or a schedule you'd like added to the app? Send us a message and we'll get
                        back to you.
                    </Typography>

                    <div className="contact-page__topics">
                        {helpTopics.map((t) => (
                            <div key={t.title} className="contact-page__topic">
                                <span className="contact-page__icon">{t.icon}</span>
                                <div>
                                    <h2>{t.title}</h2>
                                    {t.href ? <a href={t.href}>{t.body}</a> : <p>{t.body}</p>}
                                </div>
                            </div>
                        ))}
                    </div>
                </Box>

                <Paper className="contact-form-container">
                    <Typography variant="h5" component="h2" gutterBottom>
                        Send us a message
                    </Typography>

                    <form ref={formRef} onSubmit={handleSubmit}>
                        <Grid container spacing={2.5}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Your Name"
                                    name="name"
                                    variant="outlined"
                                    value={formState.name}
                                    onChange={handleChange}
                                    error={errors.name}
                                    helperText={errors.name ? 'Name is required' : ''}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Email Address"
                                    name="email"
                                    type="email"
                                    variant="outlined"
                                    value={formState.email}
                                    onChange={handleChange}
                                    error={errors.email}
                                    helperText={errors.email ? 'Please enter a valid email' : ''}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Subject"
                                    name="subject"
                                    variant="outlined"
                                    value={formState.subject}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Your Message"
                                    name="message"
                                    variant="outlined"
                                    multiline
                                    rows={6}
                                    value={formState.message}
                                    onChange={handleChange}
                                    error={errors.message}
                                    helperText={errors.message ? 'Message is required' : ''}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    className="submit-button"
                                    disabled={loading}
                                >
                                    {loading ? <CircularProgress size={24} /> : 'Send Message'}
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                </Paper>
            </Container>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default Contact;